if (!customElements.get('zoom-dialog')) {
  class ZoomDialog extends HTMLElement {
    constructor() {
      super();
      this.highResImagesLoaded = new Set();
      this.handleScroll = this.debounce(this.handleScrollEvent.bind(this), 50);

      // INP optimisation (May 2026): when rendered with `data-lazy-mount` the
      // entire dialog markup is shipped inside a single <template> element. We
      // skip every initialisation that would touch its children (which would
      // also force the browser to upgrade <drag-zoom-wrapper>, <zoom-counter>,
      // <scroll-hint>, parse <picture>/<source srcset>, etc.) and defer it to
      // the first open(). On products with many media this saves ~10s of
      // main-thread work on mobile.
      this._lazy = this.hasAttribute('data-lazy-mount') && !!this.querySelector('template[data-zoom-content]');
      this._mounted = false;

      if (this._lazy) {
        this.dialog = null;
        this.media = [];
        this.thumbnails = null;
      } else {
        this._captureRefs();
      }
    }

    _captureRefs() {
      this.dialog = this.querySelector('dialog');
      this.media = Array.from(this.querySelectorAll('.product-media-container'));
      this.thumbnails = this.querySelector('.dialog-thumbnails-list');

      if (this.dialog) {
        this.dialog.addEventListener('scroll', this.handleScroll);
      }
    }

    // Re-capture the media array after an external mutation. `this.media` is a
    // snapshot taken at mount time, so when media-gallery.appendVariantMedia
    // clones new <li> entries into the already-mounted dialog, this.media[N]
    // for the freshly appended index would be undefined. Without this refresh,
    // clicking the variant-appended slide in the main carousel opens the
    // dialog on the first media instead of the requested one.
    _refreshMediaRefs() {
      if (!this._mounted) return;
      this.media = Array.from(this.querySelectorAll('.product-media-container'));
    }

    _bindInternalTriggers() {
      this.querySelectorAll('[data-zoom-index]').forEach(trigger => {
        if (trigger._zoomBound) return;
        trigger._zoomBound = true;
        trigger.addEventListener('click', e => {
          const index = Number(trigger.dataset.zoomIndex);
          this.open(index, e);
        });
      });
    }

    async _ensureMounted() {
      if (this._mounted || !this._lazy) return;

      const template = this.querySelector('template[data-zoom-content]');
      if (!template) {
        // No template: treat as already mounted, recapture refs defensively.
        this._mounted = true;
        this._captureRefs();
        return;
      }

      const fragment = template.content.cloneNode(true);
      this.insertBefore(fragment, template);
      template.remove();

      this._captureRefs();
      this._bindInternalTriggers();
      this._mounted = true;

      // Let product-info.js (or anything else) reapply pending state once the
      // dialog DOM actually exists. The detail carries `this` for convenience.
      this.dispatchEvent(new CustomEvent('zoom-dialog:mounted', {
        bubbles: true,
        detail: { dialog: this },
      }));
    }

    connectedCallback() {
      if (!this._lazy) {
        this._bindInternalTriggers();
      }
    }

    disconnectedCallback() {
      if (this.dialog) {
        this.dialog.removeEventListener('scroll', this.handleScroll);
      }
    }

    async open(index, event) {
      event.preventDefault();

      if (this._lazy && !this._mounted) {
        await this._ensureMounted();
      }

      const targetImage = this.media[index];
      const targetThumbnail = this.thumbnails?.children[index];

      // Apply the scroll position for the requested slide. Must run AFTER
      // the dialog has been promoted to the top layer (via showModal) and
      // the browser has laid it out, otherwise scrollIntoView / scrollTo
      // are silently ignored on Chrome for several seconds while the
      // freshly mounted custom elements (drag-zoom-wrapper, zoom-counter,
      // scroll-hint, <picture>) upgrade. We schedule it with a double
      // requestAnimationFrame which is enough to clear the first paint.
      //
      // On products with many media the gallery is a horizontal flex
      // strip with `scroll-snap-type: x mandatory`; the defaults for
      // scrollIntoView use `inline: 'nearest'` and would skip the scroll
      // whenever the requested item is already partially visible, so we
      // pin `inline: 'start'`. On mobile the gallery is also forced to
      // overflow-x: hidden in base.css (see zoom-navigation.js for the
      // matching workaround on arrow/swipe nav), so scrollIntoView alone
      // does not visually move it; we mirror that explicit scrollTo here.
      const applyScroll = () => {
        for (const target of [targetThumbnail, targetImage]) {
          target?.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' });
        }
        if (targetImage && window.matchMedia('(max-width: 749px)').matches) {
          const gallery = this.querySelector('.dialog-zoomed-gallery');
          if (gallery) {
            gallery.scrollTo({ left: targetImage.offsetLeft, behavior: 'smooth' });
          }
        }
      };

      const openDialog = () => {
        if (this.dialog.showModal) {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        requestAnimationFrame(() => requestAnimationFrame(applyScroll));
      };

      const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

      // Defer selectThumbnail's scroll component for the same reason as
      // applyScroll above: opening the freshly mounted dialog blocks the
      // main thread long enough for the immediate scrollIntoView /
      // scrollTo inside selectThumbnail to be ignored.
      const runSelectThumbnail = () => {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          this.selectThumbnail(index, { behavior: 'auto' });
        }));
      };

      if (!this.supportsViewTransitions() || !sourceImage || !targetImage) {
        openDialog();
        runSelectThumbnail();
        return;
      }

      const transitionName = `gallery-item`;
      sourceImage.style.setProperty('view-transition-name', transitionName);

      await this.startViewTransition(() => {
        openDialog();
        sourceImage.style.removeProperty('view-transition-name');
        targetImage.style.setProperty('view-transition-name', transitionName);
      });

      targetImage.style.removeProperty('view-transition-name');
      runSelectThumbnail();
    }

    closeDialog() {
      if (!this.dialog) return;
      if (this.dialog.close) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      window.dispatchEvent(new CustomEvent('dialog-close'));
    }

    async close() {
      if (!this.dialog) return;
      if (!this.supportsViewTransitions()) return this.closeDialog();

      const mostVisibleElement = await this.getMostVisibleElement(this.media);
      const activeIndex = this.media.indexOf(mostVisibleElement);
      const transitionName = `gallery-item`;

      const mediaGallery = this.closest('media-gallery');
      const slideshowActive = mediaGallery?.presentation === 'carousel';
      const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

      if (!slide) return this.closeDialog();

      this.dialog.classList.add('dialog--closed');
      await this.onAnimationEnd(this.thumbnails);

      mostVisibleElement.style.setProperty('view-transition-name', transitionName);

      await this.startViewTransition(() => {
        mostVisibleElement.style.removeProperty('view-transition-name');
        slide.style.setProperty('view-transition-name', transitionName);
        this.closeDialog();
      });

      slide.style.removeProperty('view-transition-name');
      this.dialog.classList.remove('dialog--closed');
    }

    async handleScrollEvent() {
      const mostVisibleElement = await this.getMostVisibleElement(this.media);
      const activeIndex = this.media.indexOf(mostVisibleElement);
      const targetThumbnail = this.thumbnails?.children[activeIndex];

      if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

      Array.from(this.thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === activeIndex}`);
      });

      this.loadHighResolutionImage(mostVisibleElement);
      this.dispatchEvent(new CustomEvent('zoom-media:selected', { detail: { index: activeIndex } }));
    }

    async handleThumbnailClick(index) {
      const behavior = this.prefersReducedMotion() ? 'auto' : 'smooth';
      this.selectThumbnail(index, { behavior });
    }

    async selectThumbnail(index, options = { behavior: 'smooth' }) {
      if (isNaN(index) || index < 0) return;

      const hasThumbs = !!(this.thumbnails && this.thumbnails.children.length);
      if (hasThumbs && index >= this.thumbnails.children.length) return;

      if (hasThumbs) {
        const targetThumbnail = this.thumbnails.children[index];
        if (targetThumbnail instanceof HTMLElement) {
          Array.from(this.thumbnails.querySelectorAll('button')).forEach((button, i) => {
            button.setAttribute('aria-selected', `${i === index}`);
          });

          this.scrollIntoView(targetThumbnail, {
            ancestor: this.thumbnails,
            behavior: options.behavior,
            block: 'center',
            inline: 'center',
          });
        }
      }

      const targetImage = this.media[index];

      if (targetImage) {
        targetImage.scrollIntoView({
          behavior: options.behavior,
          block: 'nearest',
          inline: 'start',
        });

        // Mobile fallback: gallery has overflow-x: hidden in base.css
        // (see zoom-navigation.js for the matching workaround on
        // arrow/swipe navigation). scrollIntoView alone does not move
        // the gallery visually, so explicitly scrollTo the target item.
        if (window.matchMedia('(max-width: 749px)').matches) {
          const gallery = this.querySelector('.dialog-zoomed-gallery');
          if (gallery) {
            gallery.scrollTo({ left: targetImage.offsetLeft, behavior: options.behavior });
          }
        }

        this.loadHighResolutionImage(targetImage);
      }

      // Always notify subscribers (caption, counter, navigation) so they reflect
      // the requested index. Without this, products that initially render with a
      // single media (no dialog thumbnails) leave stale state in the dialog UI
      // after a variant change appends more media.
      this.dispatchEvent(new CustomEvent('zoom-media-selected', { detail: { index } }));
    }

    loadHighResolutionImage(mediaContainer) {
      if (!mediaContainer.classList.contains('product-media-container--image')) return false;

      const image = mediaContainer.querySelector('img.product-media__image');
      if (!image || !(image instanceof HTMLImageElement)) return false;

      const highResolutionUrl = image.getAttribute('data_max_resolution');
      if (!highResolutionUrl || this.highResImagesLoaded.has(highResolutionUrl)) return false;

      this.preloadImage(highResolutionUrl);

      const newImage = new Image();
      newImage.className = image.className;
      newImage.alt = image.alt;
      newImage.setAttribute('data_max_resolution', highResolutionUrl);

      newImage.onload = () => {
        image.replaceWith(newImage);
        this.highResImagesLoaded.add(highResolutionUrl);
      };

      newImage.src = highResolutionUrl;
    }

    getMostVisibleElement(elements) {
      return new Promise((resolve) => {
        const observer = new IntersectionObserver(
          (entries) => {
            const mostVisible = entries.reduce((prev, current) =>
              current.intersectionRatio > prev.intersectionRatio ? current : prev
            );
            observer.disconnect();
            resolve(mostVisible.target);
          },
          { threshold: [0, 0.5, 1] }
        );
        for (const element of elements) {
          observer.observe(element);
        }
      });
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    supportsViewTransitions() {
      return 'startViewTransition' in document;
    }

    async startViewTransition(callback) {
      if (this.supportsViewTransitions()) {
        return document.startViewTransition(callback);
      } else {
        callback();
      }
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    preloadImage(src) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    }

    onAnimationEnd(element) {
      return new Promise((resolve) => {
        const handleAnimationEnd = () => {
          element.removeEventListener('animationend', handleAnimationEnd);
          resolve();
        };
        element.addEventListener('animationend', handleAnimationEnd);
      });
    }

    scrollIntoView(element, { ancestor, behavior = 'smooth', block = 'start', inline = 'start' } = {}) {
      if (!ancestor) {
        return element.scrollIntoView({ behavior, block, inline });
      }

      const ancestorRect = ancestor.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const calculateScrollOffset = (alignment, ancestorStart, ancestorLength, elemStart, elemLength, currentScroll) => {
        switch (alignment) {
          case 'start':
            return currentScroll + elemStart - ancestorStart;
          case 'center':
            return currentScroll + elemStart - ancestorStart - ancestorLength / 2 + elemLength / 2;
          case 'end':
            return currentScroll + elemStart - ancestorStart - ancestorLength + elemLength;
          default:
            return currentScroll;
        }
      };

      const scrollTop =
        ancestor.scrollHeight > ancestor.clientHeight
          ? calculateScrollOffset(
              block,
              ancestorRect.top,
              ancestor.clientHeight,
              elementRect.top,
              elementRect.height,
              ancestor.scrollTop
          )
          : ancestor.scrollTop;

      const scrollLeft =
        ancestor.scrollWidth > ancestor.clientWidth
          ? Math.max(0, calculateScrollOffset(
              inline,
              ancestorRect.left,
              ancestor.clientWidth,
              elementRect.left,
              elementRect.width,
              ancestor.scrollLeft
            ))
          : ancestor.scrollLeft;

      ancestor.scrollTo({
        left: scrollLeft,
        top: scrollTop,
        behavior: behavior
      });
    }
  }

  if (!customElements.get('zoom-dialog')) {
    customElements.define('zoom-dialog', ZoomDialog);
  }
}

document.addEventListener('click', e => {
  const trigger = e.target.closest('[data-zoom-target]');
  if (!trigger) return;

  const targetSelector = trigger.dataset.zoomTarget;
  const zoomDialog = document.querySelector(targetSelector);
  if (!zoomDialog) return;

  const index = Number(trigger.dataset.zoomIndex);

  if (trigger.classList.contains('product-media-container--zoomable')) {
    zoomDialog.open(index, e);
  }

  if (trigger.classList.contains('dialog-thumbnails-list__thumbnail')) {
    zoomDialog.handleThumbnailClick(index);

    const swiperThumbnail = document.querySelector(`.swiper-controls__thumbnail[data-index="${index}"]`);
    if (swiperThumbnail) {
      swiperThumbnail.click();
    }
  }
});

if (!customElements.get('scroll-hint')) {
  class ScrollHint extends HTMLElement {
    connectedCallback() {
      this.addEventListener('scroll', theme.utils.rafThrottle(this.update.bind(this)));
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this.removeEventListener('scroll', this.update);
      this.resizeObserver.disconnect();
    }

    update = () => {
      const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = this;
      const scrollDirection = scrollWidth > clientWidth ? 'horizontal' : 'vertical';
      const scrollPercentage =
        scrollDirection === 'vertical'
          ? scrollTop / (scrollHeight - clientHeight)
          : scrollLeft / (scrollWidth - clientWidth);

      this.style.maskImage = Number.isNaN(scrollPercentage)
        ? ''
        : `linear-gradient(
          to ${scrollDirection === 'vertical' ? 'bottom' : 'right'},
          transparent ${scrollPercentage > 0 ? 1 : 0}%,
          black ${scrollPercentage < 0.1 ? scrollPercentage * 100 : 10}%,
          black ${scrollPercentage > 0.9 ? scrollPercentage * 100 : 90}%,
          transparent 100%
        )`;
    };

    resizeObserver = new ResizeObserver(this.update);
  }

  customElements.define('scroll-hint', ScrollHint);
}
